import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface User {
  id: string
  email: string
  username?: string
  full_name?: string
  role: 'user' | 'admin' | 'moderator'
  is_active: boolean
  email_verified: boolean
  created_at: string
  last_login_at?: string
  avatar_url?: string
}

export interface AuthTokens {
  token: string
  expires_at: string
}

export interface AuthState {
  // 认证状态
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // 配额信息
  quota: {
    used: number
    limit: number
    remaining: number
    reset_date: string
    usage_percentage: string
  } | null

  // 操作方法
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, username?: string, fullName?: string) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  updateProfile: (updates: Partial<User>) => Promise<void>
  refreshQuota: () => Promise<void>
  checkAuth: () => Promise<void>

  // 工具方法
  clearError: () => void
  setLoading: (loading: boolean) => void
  debugAuthState: () => AuthState
}

// API基础URL
const API_BASE = 'https://cloudflare-mutimodal-backend.steven-mei0814.workers.dev'

// 认证相关API接口
interface LoginRequest {
  email: string
  password: string
}

interface RegisterRequest {
  email: string
  password: string
  username?: string
  full_name?: string
}

// 创建认证store
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 初始状态
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      quota: null,

      // 登录
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await fetch(`${API_BASE}/api/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: email.trim(),
              password,
            } as LoginRequest),
          })

          const data = await response.json()

          if (!response.ok || !data.success) {
            throw new Error(data.error || 'Login failed')
          }

          // 确保布尔值正确转换
          const userData = {
            ...data.data.user,
            is_active: Boolean(data.data.user.is_active),
            email_verified: Boolean(data.data.user.email_verified)
          }

          // 设置认证状态
          set({
            user: userData,
            token: data.data.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })

          // 登录成功 - 暂时移除quota自动获取
          console.log('Login completed successfully, quota disabled for debugging')

          console.log('User logged in successfully:', data.data.user.email)
        } catch (error) {
          console.error('Login error:', error)
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Login failed',
          })
          throw error
        }
      },

      // 注册
      register: async (email: string, password: string, username?: string, fullName?: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await fetch(`${API_BASE}/api/auth/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: email.trim(),
              password,
              username: username?.trim(),
              full_name: fullName?.trim(),
            } as RegisterRequest),
          })

          const data = await response.json()

          if (!response.ok || !data.success) {
            throw new Error(data.error || 'Registration failed')
          }

          set({
            isLoading: false,
            error: null,
          })

          console.log('User registered successfully:', data.data.email)
        } catch (error) {
          console.error('Registration error:', error)
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Registration failed',
          })
          throw error
        }
      },

      // 登出
      logout: async () => {
        const { token } = get()

        try {
          if (token) {
            await fetch(`${API_BASE}/api/auth/logout`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            })
          }
        } catch (error) {
          console.warn('Logout API call failed, but clearing local state:', error)
        }

        // 清除本地状态
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          quota: null,
          error: null,
        })

        console.log('User logged out')
      },

      // 刷新令牌
      refreshToken: async () => {
        const { token } = get()

        if (!token) {
          throw new Error('No token available')
        }

        try {
          const response = await fetch(`${API_BASE}/api/auth/refresh`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })

          const data = await response.json()

          if (!response.ok || !data.success) {
            throw new Error(data.error || 'Token refresh failed')
          }

          set({
            token: data.data.token,
            error: null,
          })

          console.log('Token refreshed successfully')
        } catch (error) {
          console.error('Token refresh error:', error)
          // 如果刷新失败，登出用户
          await get().logout()
          throw error
        }
      },

      // 更新用户资料
      updateProfile: async (updates: Partial<User>) => {
        const { token } = get()

        if (!token) {
          throw new Error('Not authenticated')
        }

        set({ isLoading: true, error: null })

        try {
          const response = await fetch(`${API_BASE}/api/user/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(updates),
          })

          const data = await response.json()

          if (!response.ok || !data.success) {
            throw new Error(data.error || 'Profile update failed')
          }

          // 更新本地用户状态
          const { user } = get()
          if (user) {
            set({
              user: { ...user, ...updates },
              isLoading: false,
            })
          }

          console.log('Profile updated successfully')
        } catch (error) {
          console.error('Profile update error:', error)
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Profile update failed',
          })
          throw error
        }
      },

      // 刷新配额信息
      refreshQuota: async () => {
        try {
          const currentState = get();
          console.log('[QUOTA] Refreshing quota with auth state:', {
            hasToken: !!currentState.token,
            tokenLength: currentState.token?.length || 0,
            isAuthenticated: currentState.isAuthenticated
          });
          
          // Import apiClient dynamically to avoid potential circular dependency
          const { apiClient } = await import('../utils/api')
          const response = await apiClient.getUserQuota()

          if (response.success) {
            set({
              quota: response.data,
            })
            console.log('[QUOTA] Quota refreshed successfully:', response.data);
          }
        } catch (error) {
          console.warn('[QUOTA] Failed to refresh quota:', error)
        }
      },

      // 检查认证状态 - 简化版本
      checkAuth: async () => {
        const { token } = get()

        if (!token) {
          set({ isAuthenticated: false, user: null, quota: null })
          return
        }

        // API密钥格式检查（mu_开头，67字符长）
        if (!token || !token.startsWith('mu_') || token.length !== 67) {
          console.log('Invalid API key format, clearing auth state')
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            quota: null,
            error: null,
          })
          return
        }

        // 如果本地检查通过，暂时保持认证状态
        // 让实际的API调用来验证server端状态
        if (!get().user) {
          try {
            const response = await fetch(`${API_BASE}/api/auth/me`, {
              headers: { 'Authorization': `Bearer ${token}` },
            })

            if (response.ok) {
              const data = await response.json()
              if (data.success) {
                set({
                  user: {
                    ...data.data,
                    is_active: Boolean(data.data.is_active),
                    email_verified: Boolean(data.data.email_verified)
                  },
                  isAuthenticated: true,
                  error: null,
                })
                console.log('Auth check successful, quota disabled for debugging')
              }
            }
          } catch (error) {
            console.warn('Auth check failed, but keeping local state:', error)
          }
        }
      },

      // 工具方法
      clearError: () => set({ error: null }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      
      // 调试方法
      debugAuthState: () => {
        const state = get();
        console.log('[AUTH DEBUG] Current auth state:', {
          hasToken: !!state.token,
          tokenLength: state.token?.length || 0,
          tokenPrefix: state.token ? state.token.substring(0, 15) + '...' : 'NONE',
          isAuthenticated: state.isAuthenticated,
          hasUser: !!state.user,
          userEmail: state.user?.email || 'NONE'
        });
        return state;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// 导出便捷的hooks
export const useAuth = useAuthStore

// 导出认证状态检查器
export const authGuard = {
  requireAuth: () => {
    const { isAuthenticated } = useAuthStore.getState()
    return isAuthenticated
  },

  requireAdmin: () => {
    const { user } = useAuthStore.getState()
    return user?.role === 'admin'
  },

  requireModerator: () => {
    const { user } = useAuthStore.getState()
    return ['admin', 'moderator'].includes(user?.role || '')
  },
}
